import { openModal, closeModal, modalBody, onModalClose } from "./modal";
import type { CsvImportResult } from "../types";
import { esc } from "../utils/html";

interface CsvImportOptions {
  title: string;
  duplicateHint: string;
  fieldDescriptions?: { field: string; hint: string }[];
  onDownloadTemplate: () => Promise<void>;
  onImport: (file: File) => Promise<CsvImportResult>;
  onComplete: () => void;
}

export function openCsvImportModal(opts: CsvImportOptions) {
  openModal(opts.title, `
    <div style="display:flex;flex-direction:column;gap:1.2rem">
      <div>
        <strong>1. Vorlage herunterladen</strong>
        <p style="margin:.4rem 0;color:#666">Die CSV-Vorlage enthält alle Spalten als Überschrift. Fülle die Daten ein und lade die Datei hoch.</p>
        <button class="btn btn-secondary" id="csv-download">CSV-Vorlage herunterladen</button>
        ${opts.fieldDescriptions && opts.fieldDescriptions.length ? `
        <details open style="margin-top:.6rem">
          <summary style="cursor:pointer;font-size:.85rem;color:#666;user-select:none">Feldbeschreibungen</summary>
          <table style="font-size:.82rem;margin-top:.4rem;border-collapse:collapse;width:100%">
            <thead><tr>
              <th style="text-align:left;padding:2px 8px 2px 0;color:#555;font-weight:600;white-space:nowrap">Feld</th>
              <th style="text-align:left;padding:2px 0;color:#555;font-weight:600">Hinweis</th>
            </tr></thead>
            <tbody>
              ${opts.fieldDescriptions.map(d => `<tr>
                <td style="padding:2px 8px 2px 0;white-space:nowrap;color:#374151">${esc(d.field)}</td>
                <td style="padding:2px 0;color:#6b7280">${esc(d.hint)}</td>
              </tr>`).join("")}
            </tbody>
          </table>
        </details>` : ""}
      </div>
      <div>
        <strong>2. Ausgefüllte CSV-Datei hochladen</strong>
        <p style="margin:.4rem 0;color:#666">${esc(opts.duplicateHint)}</p>
        <input type="file" id="csv-file" accept=".csv" style="margin-top:.3rem" />
      </div>
      <div id="csv-result" style="display:none"></div>
      <div class="form-actions">
        <button class="btn btn-secondary" id="csv-cancel">Abbrechen</button>
        <button class="btn btn-primary" id="csv-import" disabled>Importieren</button>
      </div>
    </div>
  `);

  const mb = modalBody();
  const fileInput = mb.querySelector("#csv-file") as HTMLInputElement;
  const importBtn = mb.querySelector("#csv-import") as HTMLButtonElement;
  const resultDiv = mb.querySelector("#csv-result") as HTMLDivElement;
  let importDone = false;

  mb.querySelector("#csv-download")!.addEventListener("click", async () => {
    try { await opts.onDownloadTemplate(); } catch { alert("Download fehlgeschlagen"); }
  });

  mb.querySelector("#csv-cancel")!.addEventListener("click", closeModal);

  fileInput.addEventListener("change", () => {
    importBtn.disabled = !fileInput.files?.length;
  });

  importBtn.addEventListener("click", async () => {
    const file = fileInput.files?.[0];
    if (!file) return;

    importBtn.disabled = true;
    importBtn.textContent = "Importiere …";
    resultDiv.style.display = "none";

    try {
      const result = await opts.onImport(file);
      importDone = true;
      showSuccess(resultDiv, result);
      importBtn.style.display = "none";
      mb.querySelector("#csv-cancel")!.textContent = "Schließen";
    } catch (err: any) {
      if (err?.errors) {
        showValidationErrors(resultDiv, err);
      } else {
        showError(resultDiv, err?.error || err?.message || "Import fehlgeschlagen");
      }
      importBtn.disabled = false;
      importBtn.textContent = "Importieren";
    }
  });

  onModalClose(() => {
    if (importDone) opts.onComplete();
  });
}

function showSuccess(el: HTMLDivElement, result: CsvImportResult) {
  let html = `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:.8rem">`;
  html += `<strong style="color:#166534">${result.imported} importiert</strong>`;
  if (result.skipped && result.skipped > 0) {
    html += `, ${result.skipped} übersprungen`;
  }
  html += ` <span style="color:#666">(${result.total} Zeilen gesamt)</span>`;

  if (result.skippedRows && result.skippedRows.length > 0) {
    html += `<div style="margin-top:.5rem;font-size:.85rem;color:#666">`;
    html += `<strong>Übersprungen:</strong><ul style="margin:.3rem 0;padding-left:1.2rem">`;
    for (const s of result.skippedRows) {
      html += `<li>Zeile ${s.row}: ${esc(s.reason)}</li>`;
    }
    html += `</ul></div>`;
  }

  if (result.warnings && result.warnings.length > 0) {
    html += `<div style="margin-top:.5rem;font-size:.85rem;color:#92400e">`;
    html += `<strong>Hinweise:</strong><ul style="margin:.3rem 0;padding-left:1.2rem">`;
    for (const w of result.warnings) {
      html += `<li>Zeile ${w.row}: ${esc(w.message)}</li>`;
    }
    html += `</ul></div>`;
  }
  html += `</div>`;
  el.innerHTML = html;
  el.style.display = "block";
}

function showValidationErrors(el: HTMLDivElement, result: any) {
  let html = `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:.8rem">`;
  html += `<strong style="color:#991b1b">Validierungsfehler: kein Datensatz importiert</strong>`;
  html += `<ul style="margin:.5rem 0;padding-left:1.2rem;font-size:.85rem">`;
  for (const e of result.errors || []) {
    html += `<li>Zeile ${e.row}, ${esc(e.field)}: ${esc(e.message)}</li>`;
  }
  html += `</ul></div>`;
  el.innerHTML = html;
  el.style.display = "block";
}

function showError(el: HTMLDivElement, message: string) {
  el.innerHTML = `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:.8rem">
    <strong style="color:#991b1b">${esc(message)}</strong></div>`;
  el.style.display = "block";
}
